import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Users,
  Video,
  FileText,
  TrendingUp,
  UserCheck,
  Filter,
  Briefcase,
} from 'lucide-react'
import { adminApi } from '../../lib/api/admin'
import { formatDateInTz, formatTimeInTz, tzAbbr } from '../../lib/utils/timezone'

interface Stats {
  total_users: number
  total_sessions: number
  active_sessions: number
  total_evaluations: number
  avg_session_rating: number | null
}

interface Session {
  id: number
  client_name: string | null
  client_email: string | null
  interviewer_alias: string | null
  interviewer_name?: string
  title: string
  scheduled_at: string
  status: string
}

export default function AdminHome() {
  const navigate = useNavigate()
  const [stats, setStats] = useState<Stats>({
    total_users: 0,
    total_sessions: 0,
    active_sessions: 0,
    total_evaluations: 0,
    avg_session_rating: null,
  })
  const [sessions, setSessions] = useState<Session[]>([])
  const [loading, setLoading] = useState(true)
  const [statsLoading, setStatsLoading] = useState(false)
  const [dateRange, setDateRange] = useState('30d')
  const [timezone, setTimezone] = useState('UTC')

  // Initial load: fetch stats + recent sessions together
  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [statsData, sessionsData, pubSettings] = await Promise.all([
          adminApi.getStats({ range: dateRange }),
          adminApi.getRecentSessions(8),
          adminApi.getPublicSettings(),
        ])
        if (statsData) setStats(statsData)
        setSessions(Array.isArray(sessionsData) ? sessionsData : [])
        if (pubSettings?.timezone) setTimezone(pubSettings.timezone)
      } catch (err) {
      } finally {
        setLoading(false)
      }
    }
    fetchAll()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch only stats when date range changes (after initial load)
  useEffect(() => {
    if (loading) return // skip during initial load
    const fetchStats = async () => {
      setStatsLoading(true)
      try {
        const statsData = await adminApi.getStats({ range: dateRange })
        if (statsData) setStats(statsData)
      } catch (err) {
      } finally {
        setStatsLoading(false)
      }
    }
    fetchStats()
  }, [dateRange]) // eslint-disable-line react-hooks/exhaustive-deps

  const completionRate =
    stats.active_sessions + stats.total_sessions > 0
      ? Math.round(
          (stats.total_sessions /
            (stats.active_sessions + stats.total_sessions)) *
            100
        )
      : 0

  if (loading) {
    return <div className="loading-screen">Loading dashboard...</div>
  }

  return (
    <div>
      <div
        className="page-header"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <div>
          <h1 className="page-title">Admin Dashboard</h1>
          <p className="page-subtitle">
            Welcome back! Here is an overview of your platform.
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Filter size={16} style={{ color: 'var(--text-muted)' }} />
          <select
            className="form-input"
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            style={{ width: 'auto', padding: '8px 12px', opacity: statsLoading ? 0.6 : 1 }}
            disabled={statsLoading}
          >
            <option value="today">Today</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="1y">Last year</option>
          </select>
        </div>
      </div>

      <div className="stats-grid ah-stats-grid" style={{ opacity: statsLoading ? 0.6 : 1, transition: 'opacity 0.2s' }}>
        {[
          { icon: <Users size={24} />, iconClass: 'primary', value: stats.total_users, label: 'Total Users', sub: 'All registered users' },
          { icon: <UserCheck size={24} />, iconClass: 'success', value: stats.active_sessions, label: 'Active Sessions', sub: 'Upcoming sessions' },
          { icon: <Video size={24} />, iconClass: 'warning', value: stats.total_sessions, label: 'Total Sessions', sub: 'Completed sessions' },
          { icon: <FileText size={24} />, iconClass: 'primary', value: stats.total_evaluations, label: 'Evaluations', sub: 'Reports generated' },
        ].map((card) => (
          <div key={card.label} className="stat-card" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 16 }}>
            <div className={`icon ${card.iconClass}`} style={{ flexShrink: 0, margin: 0 }}>{card.icon}</div>
            <div>
              <div className="value" style={{ marginBottom: 2 }}>{card.value}</div>
              <div className="label">{card.label}</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{card.sub}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Metrics Row */}
      <div className="ah-metrics-grid">
        <div
          className="card"
          style={{ padding: 20 }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: 8,
            }}
          >
            <span style={{ fontSize: 14, color: 'var(--text-muted)' }}>
              Completion Rate
            </span>
            <span
              className="badge badge-success"
              style={{ fontWeight: 600 }}
            >
              {completionRate}%
            </span>
          </div>
          <div
            style={{
              height: 6,
              background: 'var(--border)',
              borderRadius: 3,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                height: '100%',
                width: `${completionRate}%`,
                background: 'var(--success)',
                borderRadius: 3,
              }}
            />
          </div>
        </div>
        <div className="card" style={{ padding: 20 }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: 8,
            }}
          >
            <span style={{ fontSize: 14, color: 'var(--text-muted)' }}>
              Avg. Session Rating
            </span>
            <span
              className="badge badge-primary"
              style={{ fontWeight: 600 }}
            >
              {stats.avg_session_rating != null ? `${stats.avg_session_rating} / 5` : 'N/A'}
            </span>
          </div>
          <div
            style={{
              height: 6,
              background: 'var(--border)',
              borderRadius: 3,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                height: '100%',
                width: stats.avg_session_rating != null ? `${(stats.avg_session_rating / 5) * 100}%` : '0%',
                background: 'var(--primary-navy)',
                borderRadius: 3,
              }}
            />
          </div>
        </div>
      </div>

      <div className="ah-main-grid">
        {/* Recent Sessions */}
        <div className="card">
          <div className="ah-sessions-header">
            <h3 style={{ fontSize: 18, fontWeight: 600 }}>
              Recent Sessions
            </h3>
            <button
              className="btn btn-secondary"
              style={{ padding: '6px 14px', fontSize: 13 }}
              onClick={() => navigate('/admin/sessions')}
            >
              View All
            </button>
          </div>

          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Interviewer</th>
                  <th>Date &amp; Time ({tzAbbr(timezone)})</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {sessions.length === 0 ? (
                  <tr>
                    <td
                      colSpan={4}
                      style={{
                        textAlign: 'center',
                        padding: 40,
                        color: 'var(--text-muted)',
                      }}
                    >
                      No sessions yet
                    </td>
                  </tr>
                ) : (
                  sessions.map((s) => {
                    const displayName = s.client_name || s.client_email || 'Unknown'
                    return (
                      <tr key={s.id}>
                        <td>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                            }}
                          >
                            <div className="avatar">
                              {displayName.charAt(0).toUpperCase()}
                            </div>
                            <span style={{ fontWeight: 500 }}>
                              {displayName}
                            </span>
                          </div>
                        </td>
                        <td>
                          {s.interviewer_alias || s.interviewer_name || 'N/A'}
                        </td>
                        <td style={{ fontSize: 13 }}>
                          <div>{formatDateInTz(s.scheduled_at, timezone)}</div>
                          <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                            {formatTimeInTz(s.scheduled_at, timezone)}
                          </div>
                        </td>
                        <td>
                          <span
                            className={`badge ${
                              s.status === 'completed'
                                ? 'badge-success'
                                : s.status === 'scheduled'
                                  ? 'badge-primary'
                                  : s.status === 'cancelled'
                                    ? 'badge-danger'
                                    : 'badge-warning'
                            }`}
                          >
                            {s.status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                          </span>
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="card">
          <h3
            style={{
              fontSize: 18,
              fontWeight: 600,
              marginBottom: 16,
            }}
          >
            Quick Actions
          </h3>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}
          >
            <button
              className="btn btn-secondary"
              style={{
                justifyContent: 'flex-start',
                padding: '14px 16px',
              }}
              onClick={() => navigate('/admin/content?tab=ai-content')}
            >
              <FileText size={18} />
              Review pending evaluations
            </button>
            <button
              className="btn btn-secondary"
              style={{
                justifyContent: 'flex-start',
                padding: '14px 16px',
              }}
              onClick={() => navigate('/admin/applications')}
            >
              <Briefcase size={18} />
              Approve interviewer applications
            </button>
            <button
              className="btn btn-secondary"
              style={{
                justifyContent: 'flex-start',
                padding: '14px 16px',
              }}
              onClick={() => navigate('/admin/reports')}
            >
              <TrendingUp size={18} />
              Generate performance report
            </button>
          </div>
        </div>
      </div>

      <style>{`
        /* Stats: 4-col on PC/tablet, 2×2 on mobile */
        .ah-stats-grid { grid-template-columns: repeat(4, 1fr) !important; }
        @media (max-width: 640px) {
          .ah-stats-grid { grid-template-columns: repeat(2, 1fr) !important; gap: 12px !important; }
        }
        @media (max-width: 480px) {
          .ah-stats-grid { gap: 10px !important; }
          .ah-stats-grid .stat-card { padding: 12px !important; gap: 10px !important; }
          .ah-stats-grid .stat-card .icon { width: 36px !important; height: 36px !important; margin: 0 !important; }
          .ah-stats-grid .stat-card .value { font-size: 22px !important; }
        }

        .ah-metrics-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
          margin-bottom: 32px;
        }
        .ah-main-grid {
          display: grid;
          grid-template-columns: 2fr 1fr;
          gap: 24px;
        }
        /* Prevent grid children from expanding beyond their track */
        .ah-main-grid > * { min-width: 0; overflow: hidden; }

        @media (max-width: 1024px) {
          .ah-main-grid { grid-template-columns: 1fr; }
        }
        @media (max-width: 768px) {
          .ah-metrics-grid { grid-template-columns: 1fr; gap: 12px; margin-bottom: 20px; }
        }

        /* Recent Sessions header: keep title + View All on one line */
        .ah-sessions-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
          gap: 8px;
        }
        .ah-sessions-header h3 { flex: 1; min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .ah-sessions-header .btn { flex-shrink: 0; }
      `}</style>
    </div>
  )
}
